import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { ASSET } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Crown, Gem, Sparkles, CalendarDays, MapPin, Gavel, Users, Lock, Award } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const TIER_ICON = {
  discepolo: Gem,
  custode: Crown,
  ambasciatore: Award,
};

const TIER_ORDER = { discepolo: 1, custode: 2, ambasciatore: 3 };

export default function Club() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [events, setEvents] = useState([]);
  const [trips, setTrips] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loadingTier, setLoadingTier] = useState(null);

  useEffect(() => {
    api.get("/club/tiers").then((r) => setTiers(r.data.tiers || []));
    api.get("/club/events").then((r) => setEvents(r.data || []));
    api.get("/club/trips").then((r) => setTrips(r.data || []));
    api.get("/club/auctions").then((r) => setAuctions(r.data || []));
  }, []);

  useEffect(() => {
    if (!user) { setMembership(null); return; }
    api.get("/club/me").then((r) => setMembership(r.data)).catch(() => {});
  }, [user]);

  const subscribe = async (tierKey) => {
    if (!user) {
      toast.info("Accedi per aderire al Club Privilegia");
      nav("/login?redirect=/club");
      return;
    }
    setLoadingTier(tierKey);
    try {
      const r = await api.post("/club/subscribe", {
        tier: tierKey,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'iscrizione");
    } finally {
      setLoadingTier(null);
    }
  };

  const userTierRank = membership?.active && membership?.membership?.tier
    ? TIER_ORDER[membership.membership.tier] || 0
    : 0;

  const tierAllows = (required) => userTierRank >= (TIER_ORDER[required] || 0);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <div data-testid="club-page" className="bg-[#0a0a0a] text-white">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/heroes/club_hero.png`} alt="" className="absolute inset-0 w-full h-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0608]/70 via-[#0a0a0a]/40 to-[#2a1e08]/40" />
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: "radial-gradient(circle at 25% 20%, #C9A961 0%, transparent 40%), radial-gradient(circle at 80% 70%, #7A1C2B 0%, transparent 45%)",
        }} />
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: "url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22n%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/%3E%3C/svg%3E')",
        }} />
        <div className="relative container-xl py-28 md:py-40">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-8">
            <div className="h-px w-8 bg-[#C9A961]" /> {t("club.hero_eyebrow")}
          </div>
          <h1 className="editorial-heading text-5xl sm:text-6xl md:text-8xl lg:text-9xl leading-[0.95] sm:leading-[0.9] font-light">
            {t("club.hero_title_1")}<br /><span className="italic text-[#C9A961]">{t("club.hero_title_2")}</span><br />{t("club.hero_title_3")}
          </h1>
          <p className="mt-8 md:mt-10 max-w-2xl text-base md:text-xl text-white/70 leading-relaxed">
            {t("club.hero_body")}
          </p>
          <div className="mt-12 flex flex-wrap gap-4 items-center">
            <a href="#tiers" className="inline-flex items-center gap-3 px-8 py-4 bg-[#C9A961] text-black font-semibold uppercase tracking-[0.2em] text-xs hover:bg-white transition-colors" data-testid="club-cta-tiers">
              <Sparkles className="h-4 w-4" /> {t("club.cta_choose_rank")}
            </a>
            <a href="#events" className="text-sm uppercase tracking-[0.3em] text-white/60 hover:text-white underline-offset-4 hover:underline" data-testid="club-cta-events">
              {t("club.cta_explore_events")} →
            </a>
          </div>

          {membership?.active && (
            <div data-testid="club-active-badge" className="mt-14 inline-flex items-center gap-4 px-6 py-4 border border-[#C9A961]/40 bg-black/40 backdrop-blur-sm">
              <Crown className="h-5 w-5 text-[#C9A961]" />
              <div className="text-sm">
                <div className="text-[#C9A961] font-semibold uppercase tracking-[0.2em] text-xs">{t("club.active_member")}</div>
                <div className="text-white/90 mt-1">
                  {t("club.tier_level")} <span className="font-bold">{membership.tier?.name}</span> · {t("club.valid_until")}{" "}
                  {formatDate(membership.membership?.expires_at)}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* TIERS */}
      <section id="tiers" className="py-24 md:py-32 border-t border-white/10">
        <div className="container-xl">
          <div className="max-w-2xl mb-20">
            <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-4">Tre soglie · una confraternita</div>
            <h2 className="editorial-heading text-5xl md:text-6xl leading-[0.95] font-light">
              Tre livelli<br /><span className="italic text-[#C9A961]">d'iniziazione.</span>
            </h2>
            <p className="mt-6 text-white/60">
              Ogni livello apre porte diverse. Scegli il grado di profondità con cui vuoi entrare nel Club.
              La quota è annuale, rinnovabile, non rimborsabile.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {tiers.map((t, idx) => {
              const Icon = TIER_ICON[t.key] || Gem;
              const isCurrent = membership?.active && membership.membership?.tier === t.key;
              const isUpgrade = userTierRank > 0 && TIER_ORDER[t.key] > userTierRank;
              const featured = t.key === "custode";
              return (
                <div
                  key={t.key}
                  data-testid={`tier-card-${t.key}`}
                  className={`relative border bg-gradient-to-b from-white/[0.02] to-transparent p-8 lg:p-10 flex flex-col ${
                    featured ? "border-[#C9A961] md:-translate-y-4 md:scale-[1.02]" : "border-white/15"
                  }`}
                  style={{ backgroundImage: featured ? `linear-gradient(180deg, ${t.color}12 0%, transparent 100%)` : undefined }}
                >
                  {featured && (
                    <div className="absolute -top-3 left-8 px-3 py-1 text-[10px] uppercase tracking-[0.3em] bg-[#C9A961] text-black font-bold">
                      Preferito
                    </div>
                  )}
                  {isCurrent && (
                    <div className="absolute -top-3 right-8 px-3 py-1 text-[10px] uppercase tracking-[0.3em] bg-green-600 text-white font-bold">
                      Tuo attuale
                    </div>
                  )}
                  <div className="flex items-start gap-4 mb-8">
                    <div className="p-3 border border-[#C9A961]/30 bg-[#C9A961]/5">
                      <Icon className="h-6 w-6 text-[#C9A961]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">Livello {idx + 1}</div>
                      <h3 className="editorial-heading text-3xl font-light mt-1">{t.name}</h3>
                      <div className="text-sm italic text-white/50 mt-1">{t.tagline}</div>
                    </div>
                  </div>
                  <div className="mb-8">
                    <div className="text-5xl font-light editorial-heading">€{t.price_annual.toLocaleString("it-IT")}</div>
                    <div className="text-xs uppercase tracking-[0.3em] text-white/40 mt-2">Quota annuale · rinnovabile</div>
                  </div>
                  <ul className="space-y-3 flex-1">
                    {t.perks.map((p, i) => (
                      <li key={i} className="flex gap-3 text-sm text-white/80">
                        <Check className="h-4 w-4 text-[#C9A961] flex-shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10">
                    {isCurrent ? (
                      <button data-testid={`tier-current-${t.key}`} disabled className="w-full py-4 bg-white/5 text-white/40 text-xs uppercase tracking-[0.3em] cursor-not-allowed">
                        Iscrizione attiva
                      </button>
                    ) : (
                      <button
                        data-testid={`tier-subscribe-${t.key}`}
                        onClick={() => subscribe(t.key)}
                        disabled={loadingTier === t.key}
                        className={`w-full py-4 text-xs uppercase tracking-[0.3em] font-semibold transition-colors ${
                          featured ? "bg-[#C9A961] text-black hover:bg-white" : "border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961] hover:text-black"
                        }`}
                      >
                        {loadingTier === t.key ? "Reindirizzamento…" : isUpgrade ? `Upgrade a ${t.name}` : `Aderisci · ${t.name}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EVENTS */}
      <section id="events" className="py-24 md:py-32 border-t border-white/10 bg-[#070707]">
        <div className="container-xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-4">Calendario esclusivo</div>
              <h2 className="editorial-heading text-5xl md:text-6xl leading-[0.95] font-light">
                Eventi<br /><span className="italic text-[#C9A961]">riservati.</span>
              </h2>
            </div>
            <p className="max-w-md text-white/60 text-sm">
              Cene nelle ville private, gala sulle pendici dell'Etna, masterclass en primeur.
              Ogni esperienza è limitata nei posti e aperta solo ai Soci del livello indicato.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {events.map((e) => {
              const locked = !tierAllows(e.tier_required);
              return (
                <article key={e.id} data-testid={`club-event-${e.id}`} className="group relative overflow-hidden border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent">
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img src={ASSET(e.image)} alt={e.title} className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 ${locked ? "opacity-40" : ""}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute top-4 left-4 flex gap-2">
                      <span className="px-3 py-1 text-[10px] uppercase tracking-[0.3em] bg-black/60 backdrop-blur border border-[#C9A961]/30 text-[#C9A961]">
                        {e.tier_required}
                      </span>
                    </div>
                    {locked && (
                      <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.3em] bg-black/70 text-white/80">
                        <Lock className="h-3 w-3" /> Riservato {e.tier_required}
                      </div>
                    )}
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="editorial-heading text-2xl md:text-3xl leading-tight">{e.title}</h3>
                      <div className="text-sm text-white/70 mt-1">{e.subtitle}</div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div><CalendarDays className="h-3.5 w-3.5 inline mr-1.5 text-[#C9A961]" />{formatDate(e.date)}</div>
                      <div><MapPin className="h-3.5 w-3.5 inline mr-1.5 text-[#C9A961]" />{e.location}</div>
                      <div><Users className="h-3.5 w-3.5 inline mr-1.5 text-[#C9A961]" />{e.seats} posti</div>
                    </div>
                    <p className="text-sm text-white/60 mt-4 leading-relaxed">{e.description}</p>
                    <div className="mt-6 flex items-center justify-between gap-3">
                      <div className="flex flex-wrap gap-1.5">
                        {(e.highlights || []).slice(0, 2).map((h, i) => (
                          <span key={i} className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-white/5 text-white/70">{h}</span>
                        ))}
                      </div>
                      <EventRSVP event={e} locked={locked} />
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRIPS */}
      <section id="trips" className="py-24 md:py-32 border-t border-white/10">
        <div className="container-xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-4">I viaggi del Club</div>
              <h2 className="editorial-heading text-5xl md:text-6xl leading-[0.95] font-light">
                Pellegrinaggi<br /><span className="italic text-[#C9A961]">del gusto.</span>
              </h2>
            </div>
            <p className="max-w-md text-white/60 text-sm">
              Bordeaux, Napa, Champagne, Borgogna. Viaggi su misura con trasferimenti privati, degustazioni verticali e accesso a cantine normalmente chiuse al pubblico.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {trips.map((t) => {
              const locked = !tierAllows(t.tier_required);
              return (
                <article key={t.id} data-testid={`club-trip-${t.id}`} className="group relative border border-white/10 bg-black/40 flex flex-col overflow-hidden">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={ASSET(t.image)} alt={t.title} className={`w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105 ${locked ? "opacity-40" : ""}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    {locked && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-black/70 text-white/80">
                        <Lock className="h-3 w-3" />{t.tier_required}
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961] mb-1">{t.duration_days} giorni · {t.destination}</div>
                      <h3 className="editorial-heading text-xl leading-tight">{t.title}</h3>
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="text-xs italic text-white/50">{t.subtitle}</div>
                    <p className="text-sm text-white/70 mt-3 flex-1">{t.description}</p>
                    <ul className="mt-4 space-y-1.5">
                      {(t.includes || []).slice(0, 3).map((inc, i) => (
                        <li key={i} className="text-xs text-white/60 flex gap-2"><Check className="h-3 w-3 text-[#C9A961] flex-shrink-0 mt-0.5" />{inc}</li>
                      ))}
                    </ul>
                    <TripInterest trip={t} locked={locked} />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* AUCTIONS */}
      <section id="auctions" className="py-24 md:py-32 border-t border-white/10 bg-[#070707]">
        <div className="container-xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-4 flex items-center gap-2"><Gavel className="h-3.5 w-3.5" /> Aste private</div>
              <h2 className="editorial-heading text-5xl md:text-6xl leading-[0.95] font-light">
                Lotti<br /><span className="italic text-[#C9A961]">ex-cellar.</span>
              </h2>
            </div>
            <p className="max-w-md text-white/60 text-sm">
              Verticali storiche, magnum d'annata, collezioni integrali. Provenance tracciata, conservazione certificata in cantine bonded.
              Solo Soci del livello richiesto possono formalizzare una richiesta di rilancio.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {auctions.map((a) => {
              const locked = !tierAllows(a.tier_required);
              return (
                <article key={a.id} data-testid={`club-auction-${a.id}`} className="group flex flex-col md:flex-row gap-0 border border-white/10 bg-black/40 overflow-hidden">
                  <div className="md:w-2/5 relative aspect-square md:aspect-auto bg-white overflow-hidden">
                    <img src={ASSET(a.image)} alt={a.lot_name} className={`w-full h-full object-contain p-4 transition-transform duration-1000 group-hover:scale-105 ${locked ? "opacity-60" : ""}`} />
                    {locked && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-black/80 text-[#C9A961]">
                        <Lock className="h-3 w-3" />{a.tier_required}
                      </div>
                    )}
                  </div>
                  <div className="md:w-3/5 p-6 flex flex-col">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">{a.producer} · {a.format}</div>
                    <h3 className="editorial-heading text-2xl leading-tight mt-2">{a.lot_name}</h3>
                    <p className="text-sm text-white/60 mt-3 flex-1 line-clamp-3">{a.description}</p>
                    <div className="mt-4 grid grid-cols-3 gap-3 text-xs border-t border-white/10 pt-4">
                      <div>
                        <div className="text-white/40 uppercase tracking-wider">{a.current_bid ? "Offerta" : "Base"}</div>
                        <div className="text-base font-bold text-[#C9A961]" data-testid={`auction-current-${a.id}`}>
                          €{Number(a.current_bid || a.starting_bid).toLocaleString("it-IT")}
                        </div>
                        {a.bid_count > 0 && <div className="text-[10px] text-white/40 mt-0.5">{a.bid_count} {a.bid_count === 1 ? "offerta" : "offerte"}</div>}
                      </div>
                      <div>
                        <div className="text-white/40 uppercase tracking-wider">Stima</div>
                        <div className="text-xs text-white/80">€{(a.estimate_min / 1000).toFixed(0)}-{(a.estimate_max / 1000).toFixed(0)}k</div>
                      </div>
                      <div>
                        <div className="text-white/40 uppercase tracking-wider">Chiude</div>
                        <div className="text-xs">{formatDate(a.ends_at)}</div>
                      </div>
                    </div>
                    <Link
                      to={`/club/auctions/${a.id}`}
                      data-testid={`auction-live-link-${a.id}`}
                      className={`mt-4 block w-full text-center px-5 py-3 text-[11px] uppercase tracking-[0.3em] font-bold transition-colors ${locked ? "bg-white/10 text-white/50 cursor-not-allowed pointer-events-none" : "bg-[#C9A961] hover:bg-white text-black"}`}
                    >
                      {locked ? `Riservato ${a.tier_required}` : (a.status === "closed" ? "Asta conclusa →" : "Asta dal vivo →")}
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="py-28 text-center border-t border-white/10">
        <div className="container-xl max-w-3xl">
          <h2 className="editorial-heading text-4xl md:text-5xl font-light leading-tight">
            L'accesso al Club è <span className="italic text-[#C9A961]">personale, non ereditabile</span>,
            e una volta dentro non si torna indietro.
          </h2>
          <p className="mt-6 text-white/60">Scegli il tuo livello d'iniziazione.</p>
          <a href="#tiers" className="mt-10 inline-flex items-center gap-3 px-10 py-5 bg-[#C9A961] text-black font-semibold uppercase tracking-[0.3em] text-xs hover:bg-white transition-colors">
            Vedi i livelli <Sparkles className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}

// Inline sub-components for RSVP / trip interest / auction bid (keep file self-contained)
function EventRSVP({ event, locked }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (locked) return toast.info(`Evento riservato ai soci ${event.tier_required}`);
    setLoading(true);
    try {
      await api.post(`/club/events/${event.id}/rsvp`, { guests: 2, note: "" });
      toast.success("Richiesta di partecipazione inviata");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setLoading(false); }
  };
  return (
    <button data-testid={`rsvp-${event.id}`} onClick={submit} disabled={loading}
      className={`text-xs uppercase tracking-[0.25em] font-semibold transition-colors ${locked ? "text-white/30" : "text-[#C9A961] hover:text-white"}`}>
      {loading ? "Invio…" : locked ? "Riservato" : "Prenota →"}
    </button>
  );
}

function TripInterest({ trip, locked }) {
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (locked) return toast.info(`Viaggio riservato ai soci ${trip.tier_required}`);
    setLoading(true);
    try {
      await api.post(`/club/trips/${trip.id}/interest`, { participants: 2 });
      toast.success("Interesse registrato · il concierge ti contatterà");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } finally { setLoading(false); }
  };
  return (
    <button data-testid={`trip-interest-${trip.id}`} onClick={submit} disabled={loading}
      className={`mt-4 w-full py-2.5 text-xs uppercase tracking-[0.25em] border transition-colors ${
        locked ? "border-white/10 text-white/30 cursor-not-allowed" : "border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961] hover:text-black"
      }`}>
      {loading ? "Invio…" : locked ? "Riservato" : "Manifesta interesse"}
    </button>
  );
}
